import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Grid, List, Plus, Trash2, GripVertical, Film, Tv, UserPlus, UserMinus, RefreshCw, MessageSquare, X, SortAsc, SortDesc, Settings, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { effectiveTier } from '../utils/entitlements';
import { useLists } from '../contexts/ListsContext';
import { supabase } from '../lib/supabase';
import { useToast } from '../components/ui/Toast';
import { getPosterUrl, calculateOurRating } from '../utils/helpers';
import { RatingModal } from '../components/RatingModal';
import { AddMovieModal } from '../components/AddMovieModal';
import { ShareButton } from '../components/ShareButton';
import { EditListModal, type PosterCandidate } from '../components/EditListModal';
import { ManageCollaboratorsModal } from '../components/ManageCollaboratorsModal';
import { SocialUpgradeModal } from '../components/ui/SocialUpgradeModal';
import { MovieCard } from '../components/MovieCard';
import { FilterBar } from '../components/FilterBar';
import { DEFAULT_FILTERS } from '../utils/constants';
import type { MediaItem, Filters } from '../types/movie';
import { useUserLibrary } from '../contexts/UserLibraryContext';
import { useFollowedWatchlists, useToggleWatchlistFollow } from '../hooks/useWatchlistFollows';
import { usePrefetchRatingsBatch } from '../hooks/usePrefetchRatingsBatch';
import {
  useWatchlistItemsInfinite,
  useOptimisticListUpdates,
  type WatchlistItemRow,
  type WatchlistMeta,
} from '../hooks/useListsQuery';
import { apiFetch, ApiError } from '../lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { enqueueReorderIntent, removeIntent, removeIntentIfMatches, getOutboxIntents, getReorderIntentKeyForLookup } from '../lib/outbox';
import { usePersistFlush } from '../components/QueryCachePersister';

type MediaKey = `${'movie' | 'tv'}:${number}`;

interface ListEntryMeta {
  itemId: string;
  position: number;
  notes?: string;
  added_at: string;
}

function getMediaKey(mediaId: number, mediaType: 'movie' | 'tv'): MediaKey {
  return `${mediaType}:${mediaId}`;
}

// Extended item with enriched data from Meilisearch
// Note: runtime is inherited from WatchlistItemRow as `number | null | undefined`
interface EnrichedWatchlistItem extends WatchlistItemRow {
  genre_ids?: number[];
  imdb_rating?: number;
  rt_rating?: number;
  filmpl_rating?: number;
  popularity?: number;
  original_language?: string;
  vote_count?: number;
}

type SortOption = 'position' | 'added_at' | 'media_title' | 'media_year' | 'popularity' | 'filmpl_rating' | 'imdb_rating' | 'rt_rating' | 'runtime';
type ViewMode = 'grid' | 'list';
type MediaTypeFilter = 'all' | 'movie' | 'tv';

// Sort options configuration
const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'position', label: 'Custom Order' },
  { value: 'added_at', label: 'Date Added' },
  { value: 'media_title', label: 'Title' },
  { value: 'media_year', label: 'Year' },
  { value: 'popularity', label: 'Most Popular' },
  { value: 'filmpl_rating', label: 'Filmple Rating' },
  { value: 'imdb_rating', label: 'IMDb Rating' },
  { value: 'rt_rating', label: 'Rotten Tomatoes' },
  { value: 'runtime', label: 'Runtime' },
];

// Convert EnrichedWatchlistItem to MediaItem for MovieCard
function toMediaItem(item: EnrichedWatchlistItem): MediaItem {
  return {
    id: item.media_id,
    title: item.media_title,
    overview: '',
    poster_path: item.media_poster || null,
    backdrop_path: null,
    release_date: item.media_year ? `${item.media_year}-01-01` : '',
    genre_ids: item.genre_ids || [],
    vote_average: 0,
    vote_count: 0,
    popularity: item.popularity || 0,
    original_language: '',
    runtime: item.runtime ?? undefined, // normalize null to undefined for UI
    media_type: item.media_type,
    imdb_rating: item.imdb_rating,
    rt_rating: item.rt_rating,
  };
}

// ============================================================================
// Sort Persistence Helpers (localStorage + cross-tab + server sync)
// ============================================================================

const SORT_KEY_PREFIX = 'filmple:watchlistSort:';

// Server-syncable sort values (persisted to DB)
const SERVER_SORT_VALUES = new Set(['position', 'added_at', 'media_title', 'media_year', 'popularity', 'filmpl_rating', 'imdb_rating', 'rt_rating', 'runtime']);

interface StoredSort {
  sortBy: SortOption;
  sortOrder: 'asc' | 'desc';
  updatedAt: string; // ISO timestamp for server reconciliation
}

function getSortStorageKey(userId: string, listId: string): string {
  return `${SORT_KEY_PREFIX}${userId}:${listId}`;
}

function loadStoredSort(userId: string | undefined, listId: string | undefined): StoredSort | null {
  if (!userId || !listId) return null;
  try {
    const raw = localStorage.getItem(getSortStorageKey(userId, listId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSort;
    if (parsed.sortBy && parsed.sortOrder) return parsed;
    return null;
  } catch {
    return null;
  }
}

function saveStoredSort(userId: string, listId: string, sortBy: SortOption, sortOrder: 'asc' | 'desc', updatedAtIso?: string): void {
  try {
    const data: StoredSort = { sortBy, sortOrder, updatedAt: updatedAtIso ?? new Date().toISOString() };
    localStorage.setItem(getSortStorageKey(userId, listId), JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
}

export function WatchlistPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, session, profile, initialized, loading: authLoading } = useAuth();
  const isFree = !!profile && effectiveTier(profile) === 'free';
  const authReady = !user || !!session?.access_token;

  // Sort state: initialized from localStorage for instant restore (no flash)
  const [sortBy, setSortByRaw] = useState<SortOption>(() => {
    const stored = loadStoredSort(user?.id, id);
    return stored?.sortBy ?? 'position';
  });
  const [sortOrder, setSortOrderRaw] = useState<'asc' | 'desc'>(() => {
    const stored = loadStoredSort(user?.id, id);
    return stored?.sortOrder ?? 'asc';
  });

  // Wrapped setters (support both direct value and updater function form)
  const setSortBy = useCallback((value: SortOption | ((prev: SortOption) => SortOption)) => {
    setSortByRaw(value);
  }, []);
  const setSortOrder = useCallback((value: 'asc' | 'desc' | ((prev: 'asc' | 'desc') => 'asc' | 'desc')) => {
    setSortOrderRaw(value);
  }, []);

  // Late-auth rehydrate: when user?.id arrives after first render, re-read localStorage
  const localRehydrateDone = useRef<string | null>(null); // tracks "userId:listId"
  useEffect(() => {
    if (!user?.id || !id) return;
    const compositeKey = `${user.id}:${id}`;
    if (localRehydrateDone.current === compositeKey) return;
    localRehydrateDone.current = compositeKey;

    const stored = loadStoredSort(user.id, id);
    if (stored) {
      setSortByRaw((prev) => (prev !== stored.sortBy ? stored.sortBy : prev));
      setSortOrderRaw((prev) => (prev !== stored.sortOrder ? stored.sortOrder : prev));
    }
  }, [user?.id, id]);

  // Persist to localStorage whenever sort changes (also triggers cross-tab via storage event)
  const sortPersistInitialized = useRef(false);
  useEffect(() => {
    if (!user?.id || !id) return;
    // Skip initial mount (already loaded from localStorage)
    if (!sortPersistInitialized.current) {
      sortPersistInitialized.current = true;
      return;
    }
    saveStoredSort(user.id, id, sortBy, sortOrder);
  }, [user?.id, id, sortBy, sortOrder]);

  // Cross-tab sync: listen to storage events
  // Refs to avoid stale closures in the event handler
  const sortByRef = useRef(sortBy);
  const sortOrderRef = useRef(sortOrder);
  useEffect(() => { sortByRef.current = sortBy; }, [sortBy]);
  useEffect(() => { sortOrderRef.current = sortOrder; }, [sortOrder]);

  useEffect(() => {
    if (!user?.id || !id) return;
    const key = getSortStorageKey(user.id, id);

    const handleStorage = (e: StorageEvent) => {
      if (e.key !== key || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue) as StoredSort;
        // Guard: only update if actually different (prevents loops)
        if (parsed.sortBy && parsed.sortBy !== sortByRef.current) setSortByRaw(parsed.sortBy);
        if (parsed.sortOrder && parsed.sortOrder !== sortOrderRef.current) setSortOrderRaw(parsed.sortOrder);
      } catch { /* ignore malformed */ }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [user?.id, id]);

  // Server sync: GET preferences on mount, reconcile (server wins if newer)
  const serverSyncDone = useRef(false);
  useEffect(() => {
    if (!user?.id || !id || !session?.access_token) return;
    if (serverSyncDone.current) return;
    serverSyncDone.current = true;

    (async () => {
      try {
        const result = await apiFetch<{ preferences: { sort_by: string; sort_order: string; updated_at: string } | null }>(
          `/watchlists/${id}/preferences`,
          { token: session.access_token }
        );
        if (result.preferences) {
          const serverSort = result.preferences;
          const stored = loadStoredSort(user.id, id);
          const serverTime = new Date(serverSort.updated_at).getTime();
          const localTime = stored?.updatedAt ? new Date(stored.updatedAt).getTime() : 0;

          // Server wins if newer (uses server timestamps on both sides — clock-skew safe)
          if (serverTime > localTime) {
            const sb = serverSort.sort_by as SortOption;
            const so = serverSort.sort_order as 'asc' | 'desc';
            setSortByRaw(sb);
            setSortOrderRaw(so);
            // Write server's updated_at into localStorage (clock-skew safe)
            saveStoredSort(user.id, id, sb, so, serverSort.updated_at);
          }
        }
      } catch {
        // Non-fatal: local sort is fine
      }
    })();
  }, [user?.id, id, session?.access_token]);

  // Server sync: debounced PATCH on sort change — writes back server updated_at
  const patchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!user?.id || !id || !session?.access_token) return;
    // Skip initial mount
    if (!sortPersistInitialized.current) return;

    // Only sync server-supported sort values
    if (!SERVER_SORT_VALUES.has(sortBy)) return;

    // Capture current values for the async callback
    const capturedUserId = user.id;
    const capturedId = id;
    const capturedSortBy = sortBy;
    const capturedSortOrder = sortOrder;

    if (patchTimerRef.current) clearTimeout(patchTimerRef.current);
    patchTimerRef.current = setTimeout(async () => {
      try {
        const result = await apiFetch<{ success: boolean; preferences: { sort_by: string; sort_order: string; updated_at: string } }>(
          `/watchlists/${capturedId}/preferences`,
          {
            method: 'PATCH',
            token: session.access_token!,
            body: { sort_by: capturedSortBy, sort_order: capturedSortOrder },
          }
        );
        // Write back server's updated_at for clock-skew-safe reconciliation
        if (result.preferences?.updated_at) {
          saveStoredSort(capturedUserId, capturedId, capturedSortBy, capturedSortOrder, result.preferences.updated_at);
        }
      } catch { /* non-fatal */ }
    }, 1000); // 1s debounce

    return () => {
      if (patchTimerRef.current) clearTimeout(patchTimerRef.current);
    };
  }, [user?.id, id, session?.access_token, sortBy, sortOrder]);

  // Reset sync flags when watchlist ID changes (navigating between lists)
  useEffect(() => {
    serverSyncDone.current = false;
    sortPersistInitialized.current = true;
    localRehydrateDone.current = null;
  }, [id]);

  // Phase 4A: React Query is the source of truth for list items
  // Derive server-side sort params from UI sort:
  //   - 'position' → server sort by position asc (canonical custom order)
  //   - 'added_at' → server sort by added_at with user's chosen order
  //   - everything else (ratings, popularity, etc.) → default added_at desc; UI sorts client-side
  const serverSortBy: 'position' | 'added_at' = sortBy === 'position' ? 'position' : sortBy === 'added_at' ? 'added_at' : 'added_at';
  const serverSortOrder: 'asc' | 'desc' = sortBy === 'position' ? 'asc' : sortBy === 'added_at' ? sortOrder : 'desc';

  const {
    watchlist: rqWatchlist,
    items: rqItems,
    isLoading: rqLoading,
    isFetching: rqFetching,
    isFetchingNextPage,
    hasNextPage,
    error: rqError,
    fetchNextPage,
    refetch: rqRefetch,
  } = useWatchlistItemsInfinite(id, {
    enabled: !!id && initialized && !authLoading && authReady,
    sortBy: serverSortBy,
    sortOrder: serverSortOrder,
  });

  // Optimistic update utilities for mutations
  const optimisticUpdates = useOptimisticListUpdates();
  // Note: invalidateMine no longer needed here - toggleListItem handles it for custom lists

  // Keep ListsContext for list metadata lookups
  const {
    lists: listsFromContext,
    getListMeta,
    setListItemDesired,
  } = useLists();

  // React Query hooks for follow state (Phase 3.1 - single source of truth)
  const { followedLists, isLoading: followedListsLoading } = useFollowedWatchlists();
  const { toggleFollow, isLoading: followToggleLoading } = useToggleWatchlistFollow();
  const { toast } = useToast();
  const tokenRef = useRef<string | null>(null);
  useEffect(() => {
    tokenRef.current = session?.access_token ?? null;
  }, [session?.access_token]);

  // Serialized move queue: ensures only one reorder request is in flight at a time
  // and always uses the latest known revision.
  const revisionRef = useRef<number>(0);
  const moveInFlightRef = useRef(false);
  const pendingMovesRef = useRef<Array<{
    movedItemId: string;
    insertBeforeId: string | null;
    intentKey: string;
    idempotencyKey: string;
  }>>([]);
  const invalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didAckRef = useRef(false); // Track if any move was ACK'd during this flush
  const { flushNow } = usePersistFlush();
  const [isReorderFlushing, setIsReorderFlushing] = useState(false);
  const isReorderFlushingRef = useRef(false);
  useEffect(() => { isReorderFlushingRef.current = isReorderFlushing; }, [isReorderFlushing]);
  const consecutiveFlushFailuresRef = useRef(0);

  const {
    toggleWatched,
    toggleWatchlist,
    toggleFavorites,
    isHydrated,
  } = useUserLibrary();

  // Watchlist metadata: prefer React Query, fall back to ListsContext for my lists
  const watchlist = useMemo((): WatchlistMeta | null => {
    if (!id) return null;
    // React Query has the freshest data
    if (rqWatchlist) return rqWatchlist;
    // Fall back to ListsContext (for sidebar lists)
    const fromStore = getListMeta(id);
    if (fromStore) return fromStore;
    const fromCtx = listsFromContext.find((w) => w.id === id) || null;
    return fromCtx;
  }, [id, rqWatchlist, getListMeta, listsFromContext]);

  // Keep revisionRef synced with cache (only bump forward, never regress)
  useEffect(() => {
    const rev = watchlist?.revision;
    if (typeof rev === 'number' && rev > revisionRef.current) {
      revisionRef.current = rev;
    }
  }, [watchlist?.revision]);

  // Entry meta for notes (derived from items)
  const entryMeta = useMemo(() => {
    const map = new Map<MediaKey, ListEntryMeta>();
    rqItems.forEach((item) => {
      const key = getMediaKey(item.media_id, item.media_type);
      map.set(key, {
        itemId: item.id,
        position: item.position ?? 0,
        notes: item.notes ?? undefined,
        added_at: item.added_at,
      });
    });
    return map;
  }, [rqItems]);
  const [error, setError] = useState<string | null>(null);

  // FilterBar filters
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [mediaTypeFilter, setMediaTypeFilter] = useState<MediaTypeFilter>('all');
  const [popularOnly, setPopularOnly] = useState(false);


  const [selectedMovie, setSelectedMovie] = useState<MediaItem | null>(null);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [showAddMovieModal, setShowAddMovieModal] = useState(false);
  const [showEditListModal, setShowEditListModal] = useState(false);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
  const [upgradeModalFeature, setUpgradeModalFeature] = useState<string | null>(null);

  // Note editing state
  const [editingNoteItemId, setEditingNoteItemId] = useState<MediaKey | null>(null);
  const [noteText, setNoteText] = useState<string>('');

  // Drag & drop state
  const [draggedKey, setDraggedKey] = useState<MediaKey | null>(null);
  const [dropPosition, setDropPosition] = useState<number | null>(null);
  const dragCounter = useRef(0);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  // Infinite scroll sentinel ref + observer
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const handleLoadMoreRef = useRef<() => void>(() => {});

  // Double-click guard for remove operations
  const removingItems = useRef<Set<MediaKey>>(new Set());

  // Phase 4A: React Query handles fetching - derive error from RQ
  useEffect(() => {
    if (rqError) {
      const msg = rqError.message || 'Failed to load watchlist';
      if (msg.includes('404')) {
        setError('Watchlist not found');
      } else if (msg.includes('403') || msg.includes('401')) {
        setError("You don't have access to this watchlist");
      } else {
        setError(msg);
      }
    } else {
      setError(null);
    }
  }, [rqError]);

  // Focus revalidate: React Query handles SWR - just trigger refetch on focus
  useEffect(() => {
    if (!id) return;
    const onVis = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      // Don't refetch during reorder — optimistic state is authoritative
      if (isReorderFlushingRef.current || pendingMovesRef.current.length > 0) return;
      rqRefetch();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [id, rqRefetch]);

  // Infinite scroll: IntersectionObserver for sentinel
  useEffect(() => {
    if (!watchlist) return;
    if (!hasNextPage || isFetchingNextPage) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleLoadMoreRef.current();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [watchlist, hasNextPage, isFetchingNextPage]);

  // List owner: derived from watchlist.owner (comes from API response)
  const listOwner = useMemo(() => {
    if (!watchlist) return null;
    if (watchlist.owner) {
      return {
        id: watchlist.owner.id || watchlist.user_id || '',
        username: watchlist.owner.username,
        display_name: watchlist.owner.display_name,
      };
    }
    if (watchlist.user_id) {
      return { id: watchlist.user_id };
    }
    return null;
  }, [watchlist]);

  // List updated at: derived from watchlist
  const listUpdatedAt = watchlist?.updated_at ?? null;

  // Fetch owner profile if username is missing (for non-owner default list titles)
  const [fetchedOwnerProfile, setFetchedOwnerProfile] = useState<{ username?: string; display_name?: string } | null>(null);
  useEffect(() => {
    async function fetchOwnerProfile() {
      if (!listOwner?.id) return;
      // Skip if we already have username from watchlist.owner
      if (listOwner.username) return;
      // Skip if this is the current user
      if (user?.id === listOwner.id) return;

      // Use profiles_public for identity-only lookup (always readable)
      const { data } = await supabase
        .from('profiles_public')
        .select('username, display_name')
        .eq('id', listOwner.id)
        .maybeSingle();

      if (data) {
        setFetchedOwnerProfile(data);
      }
    }

    fetchOwnerProfile();
  }, [listOwner?.id, listOwner?.username, user?.id]);

  // Merge fetched profile with listOwner
  const effectiveListOwner = useMemo(() => {
    if (!listOwner) return null;
    if (listOwner.username) return listOwner;
    if (fetchedOwnerProfile) {
      return { ...listOwner, ...fetchedOwnerProfile };
    }
    return listOwner;
  }, [listOwner, fetchedOwnerProfile]);

  // Permission model (Spotify-like):
  // - owner: full control
  // - editor collaborator: edit items + notes + reorder
  // - viewer collaborator: read-only
  const isOwner = useMemo(() => !!user?.id && !!effectiveListOwner?.id && effectiveListOwner.id === user.id, [effectiveListOwner?.id, user?.id]);
  const collaboratorRole = watchlist?.collaborator_role as 'editor' | 'viewer' | undefined;
  const canEdit = !!user?.id && (isOwner || collaboratorRole === 'editor');

  // Prefetch collaborator data so modal opens warm (deduped by React Query)
  useEffect(() => {
    if (!watchlist?.id || !session?.access_token || watchlist.is_default) return;
    const isEligibleOwner = isOwner && !isFree;
    const isCollaborator = !!collaboratorRole;
    if (!isEligibleOwner && !isCollaborator) return;
    const token = session.access_token;
    queryClient.prefetchQuery({
      queryKey: queryKeys.lists.collaborators(watchlist.id),
      queryFn: () => apiFetch<{ collaborators: unknown[] }>(`/watchlists/${watchlist.id}/collaborators`, { token }),
      staleTime: 60_000,
    });
  }, [watchlist?.id, watchlist?.is_default, isOwner, isFree, collaboratorRole, session?.access_token, queryClient]);

  // Display title: "@username's Watchlist" for non-owner default lists
  const displayTitle = useMemo(() => {
    if (!watchlist?.name) return '';
    // Non-owner viewing a default list: show "@username's Watchlist"
    if (watchlist.is_default && !isOwner && effectiveListOwner?.username) {
      return `@${effectiveListOwner.username}'s ${watchlist.name}`;
    }
    return watchlist.name;
  }, [watchlist?.name, watchlist?.is_default, isOwner, effectiveListOwner?.username]);

  // Follow state (Phase 3.1 - derived from React Query cache)
  const followState = useMemo<'unknown' | 'following' | 'not_following'>(() => {
    if (!watchlist?.id) return 'unknown';
    if (!session?.access_token) return 'unknown';
    if (followedListsLoading) return 'unknown';
    return followedLists.some((l) => l.id === watchlist.id) ? 'following' : 'not_following';
  }, [watchlist?.id, session?.access_token, followedListsLoading, followedLists]);

  const isFollowing = followState === 'following';

  // Phase 4A: Items come directly from React Query (rqItems)
  // Items are already enriched from the backend (genre_ids, runtime, ratings, etc.)
  const items = useMemo(() => {
    return rqItems as EnrichedWatchlistItem[];
  }, [rqItems]);

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = [...items];

    // Filter by media type
    if (mediaTypeFilter !== 'all') {
      filtered = filtered.filter(item => item.media_type === mediaTypeFilter);
    }

    // Popular gate v1: (vote_count >= 50 OR popularity >= 4)
    // Same canonical gate used by Discover and /feel
    if (popularOnly) {
      filtered = filtered.filter(item => {
        return (item.vote_count ?? 0) >= 50 || (item.popularity ?? 0) >= 4;
      });
    }

    // FilterBar filters
    // Genre filter
    if (filters.genres.length > 0) {
      filtered = filtered.filter(item => {
        const enriched = item as EnrichedWatchlistItem;
        return filters.genres.some(g => enriched.genre_ids?.includes(g));
      });
    }

    // Year range filter
    if (filters.yearRange[0] > 1920 || filters.yearRange[1] < new Date().getFullYear()) {
      filtered = filtered.filter(item => {
        if (!item.media_year) return false;
        return item.media_year >= filters.yearRange[0] && item.media_year <= filters.yearRange[1];
      });
    }

    // Runtime filter
    if (filters.runtimeRange[0] > 0 || filters.runtimeRange[1] < 300) {
      filtered = filtered.filter(item => {
        const enriched = item as EnrichedWatchlistItem;
        if (!enriched.runtime) return filters.runtimeRange[0] === 0;
        return enriched.runtime >= filters.runtimeRange[0] && enriched.runtime <= filters.runtimeRange[1];
      });
    }

    // Our rating filter
    if (filters.ourRatingRange[0] > 0 || filters.ourRatingRange[1] < 10) {
      filtered = filtered.filter(item => {
        const rating = calculateOurRating(item.imdb_rating, item.rt_rating) || 0;
        return rating >= filters.ourRatingRange[0] && rating <= filters.ourRatingRange[1];
      });
    }

    // IMDb rating filter
    if (filters.ratingRange[0] > 0 || filters.ratingRange[1] < 10) {
      filtered = filtered.filter(item => {
        const rating = item.imdb_rating || 0;
        return rating >= filters.ratingRange[0] && rating <= filters.ratingRange[1];
      });
    }

    // RT rating filter
    if (filters.rtRatingRange[0] > 0 || filters.rtRatingRange[1] < 100) {
      filtered = filtered.filter(item => {
        const rating = item.rt_rating || 0;
        return rating >= filters.rtRatingRange[0] && rating <= filters.rtRatingRange[1];
      });
    }

    // Language filter
    if (filters.languages.length > 0) {
      filtered = filtered.filter(item => {
        const enriched = item as EnrichedWatchlistItem;
        // Check if item's original_language matches any selected language
        return enriched.original_language && filters.languages.includes(enriched.original_language);
      });
    }

    // Sort items: position and added_at are server-canonical (skip client sort).
    // All other sorts (ratings, popularity, runtime, title, year) are client-side.
    const shouldSkipSort = sortBy === 'position' || sortBy === 'added_at';
    if (!shouldSkipSort) {
    const sortMultiplier = sortOrder === 'asc' ? 1 : -1;
    filtered.sort((a, b) => {
      const enrichedA = a as EnrichedWatchlistItem;
      const enrichedB = b as EnrichedWatchlistItem;

      switch (sortBy) {
        case 'media_title':
          return (a.media_title || '').localeCompare(b.media_title || '') * sortMultiplier;
        case 'media_year':
          return ((a.media_year || 0) - (b.media_year || 0)) * sortMultiplier;
        case 'popularity':
          return ((enrichedB.popularity || 0) - (enrichedA.popularity || 0)) * sortMultiplier;
        case 'filmpl_rating': {
          const ratingA = enrichedA.filmpl_rating ?? calculateOurRating(enrichedA.imdb_rating, enrichedA.rt_rating) ?? 0;
          const ratingB = enrichedB.filmpl_rating ?? calculateOurRating(enrichedB.imdb_rating, enrichedB.rt_rating) ?? 0;
          return (ratingB - ratingA) * sortMultiplier;
        }
        case 'imdb_rating':
          return ((enrichedB.imdb_rating || 0) - (enrichedA.imdb_rating || 0)) * sortMultiplier;
        case 'rt_rating':
          return ((enrichedB.rt_rating || 0) - (enrichedA.rt_rating || 0)) * sortMultiplier;
        case 'runtime':
          return ((enrichedA.runtime || 0) - (enrichedB.runtime || 0)) * sortMultiplier;
        default:
          return 0;
      }
    });
    }

    return filtered;
  }, [items, mediaTypeFilter, popularOnly, filters, sortBy, sortOrder, watchlist]);

  const isPartialClientSort = useMemo(() => {
    const clientSortOptions = new Set(['popularity', 'filmpl_rating', 'imdb_rating', 'rt_rating', 'runtime', 'media_title', 'media_year']);
    return clientSortOptions.has(sortBy) && hasNextPage;
  }, [sortBy, hasNextPage]);

  // Batch prefetch ratings for visible items (prevents per-card waterfall)
  const ratingItems = useMemo(
    () => filteredItems.slice(0, 100).map((item) => ({ media_id: item.media_id, media_type: item.media_type })),
    [filteredItems]
  );
  usePrefetchRatingsBatch(ratingItems);

  // Determine if filtering is active (for showing "X items found" row)
  const isFilteredView = useMemo(() => {
    // Check if filteredItems differs from items
    if (filteredItems.length !== items.length) return true;
    // Check page-level filters
    if (mediaTypeFilter !== 'all') return true;
    if (popularOnly) return true;
    // Check FilterBar filters against defaults
    if (filters.genres.length > 0) return true;
    if (filters.languages.length > 0) return true;
    const currentYear = new Date().getFullYear();
    if (filters.yearRange[0] !== 1920 || filters.yearRange[1] !== currentYear) return true;
    if (filters.runtimeRange[0] !== 0 || filters.runtimeRange[1] !== 300) return true;
    if (filters.ourRatingRange[0] !== 0 || filters.ourRatingRange[1] !== 10) return true;
    if (filters.ratingRange[0] !== 0 || filters.ratingRange[1] !== 10) return true;
    if (filters.rtRatingRange[0] !== 0 || filters.rtRatingRange[1] !== 100) return true;
    return false;
  }, [filteredItems.length, items.length, mediaTypeFilter, popularOnly, filters]);

  // Count movies and TV shows
  const movieCount = useMemo(() => items.filter(i => i.media_type === 'movie').length, [items]);
  const tvCount = useMemo(() => items.filter(i => i.media_type === 'tv').length, [items]);

  const lastKnownCountsRef = useRef({ movieCount: 0, tvCount: 0 });

  useEffect(() => {
    // Reset when navigating to a different list
    lastKnownCountsRef.current = { movieCount: 0, tvCount: 0 };
  }, [id]);

  useEffect(() => {
    if (items.length > 0) {
      lastKnownCountsRef.current = { movieCount, tvCount };
    }
  }, [items.length, movieCount, tvCount]);

  const stableMovieCount = items.length > 0 ? movieCount : lastKnownCountsRef.current.movieCount;
  const stableTvCount = items.length > 0 ? tvCount : lastKnownCountsRef.current.tvCount;

  // Existing media IDs for AddMovieModal
  const existingMediaIds = useMemo(() =>
    new Set(items.map(item => `${item.media_type}-${item.media_id}`)),
    [items]
  );

  // Poster candidates for EditListModal (dedupe by poster_path, cap to 24)
  const posterCandidates: PosterCandidate[] = useMemo(() => {
    const seen = new Set<string>();
    const candidates: PosterCandidate[] = [];
    for (const item of items) {
      if (item.media_poster && !seen.has(item.media_poster)) {
        seen.add(item.media_poster);
        candidates.push({
          poster_path: item.media_poster,
          title: item.media_title || 'Unknown',
        });
        if (candidates.length >= 24) break;
      }
    }
    return candidates;
  }, [items]);

  // Followers are eventually consistent (plan): no realtime subscription here.

  // Followers are eventually consistent (plan): no realtime for followers.
  // Keep manual refresh button + updated_at indicator.

  // Format relative time
  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // NOTE: Public lists must be viewable by logged-out users (plan).

  const handleRemoveItem = async (mediaKey: MediaKey) => {
    if (!watchlist || !id) return;

    const [mediaType, mediaIdStr] = mediaKey.split(':') as ['movie' | 'tv', string];
    const mediaId = parseInt(mediaIdStr, 10);

    if (removingItems.current.has(mediaKey)) return;
    removingItems.current.add(mediaKey);

    const item = items.find(i => i.media_id === mediaId && i.media_type === mediaType);
    if (!item) {
      removingItems.current.delete(mediaKey);
      return;
    }

    try {
      if (watchlist.is_default && watchlist.slug) {
        // DEFAULT LISTS: Use UserLibraryContext toggles
        // Phase 4A: Optimistic removal via React Query cache (for default lists only)
        optimisticUpdates.removeItem(id, mediaId, mediaType);

        if (!user) throw new Error('Not authenticated');

        switch (watchlist.slug) {
          case 'watched':
            await toggleWatched(
              mediaId,
              mediaType,
              item.media_title,
              item.media_poster ?? undefined,
              item.media_year ?? undefined
            );
            break;
          case 'watchlist':
            await toggleWatchlist(
              mediaId,
              mediaType,
              item.media_title,
              item.media_poster ?? undefined,
              item.media_year ?? undefined
            );
            break;
          case 'favorites':
            await toggleFavorites(
              mediaId,
              mediaType,
              item.media_title,
              item.media_poster ?? undefined,
              item.media_year ?? undefined
            );
            break;
        }
        // Success - toggles now throw on failure
        toast.success('Removed from list');
      } else {
        // CUSTOM LISTS: Use setListItemDesired for SPAM-SAFE removal
        // Uses idempotent DELETE by-media endpoint (no item_id lookup needed)
        // Handles: list-items cache, lists.mine count, membership caches, rollback

        // Proactive gate: Free users cannot modify custom lists
        if (isFree) {
          setUpgradeModalFeature('Removing from custom lists');
          return;
        }

        // Guard: temp- IDs mean add is still pending
        if (!item.id || item.id.startsWith('temp-')) {
          toast.error('Cannot remove - item still syncing');
          return;
        }

        // Use setListItemDesired with desiredInList=false for deterministic removal
        const result = await setListItemDesired(
          id,
          mediaId,
          mediaType,
          item.media_title,
          false, // desiredInList = false (we want to remove)
          item.media_poster ?? undefined,
          item.media_year ?? undefined,
          undefined // enrichment not needed for removal
        );

        // Only show toast on real state flips (not no-op)
        if (result.didChange) {
          toast.success('Removed from list');
        }
      }
    } catch (err) {
      console.error('Error removing item:', err);
      // Note: setListItemDesired handles rollback internally via onError
      // For default lists, refetch to ensure consistency
      if (watchlist.is_default) {
        await rqRefetch();
      }
      // Show upgrade modal for tier-gated errors instead of generic error
      if (err instanceof ApiError && err.code === 'SOCIAL_TIER_REQUIRED') {
        setUpgradeModalFeature('Removing from custom lists');
      } else {
        toast.error('Something went wrong');
      }
    } finally {
      removingItems.current.delete(mediaKey);
    }
  };

  // Phase 4A: Load more uses React Query's fetchNextPage
  const handleLoadMore = async () => {
    if (!watchlist || isFetchingNextPage || !hasNextPage) return;
    try {
      await fetchNextPage();
    } catch (err) {
      console.error('Failed to load more:', err);
      toast.error('Failed to load more');
    }
  };

  // Keep ref current for IntersectionObserver callback (avoids stale closure)
  handleLoadMoreRef.current = handleLoadMore;

  // Drag & drop handlers
  const handleDragStart = (e: React.DragEvent, item: WatchlistItemRow) => {
    const key = getMediaKey(item.media_id, item.media_type);
    setDraggedKey(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
    setTimeout(() => {
      (e.target as HTMLElement).classList.add('opacity-50');
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).classList.remove('opacity-50');
    setDraggedKey(null);
    setDropPosition(null);
    dragCounter.current = 0;
  };

  const handleContainerDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedKey || filteredItems.length === 0) return;

    const draggedIndex = filteredItems.findIndex((item) => getMediaKey(item.media_id, item.media_type) === draggedKey);
    const isGrid = viewMode === 'grid';

    if (isGrid) {
      const itemsOnRow: { index: number; rect: DOMRect }[] = [];

      filteredItems.forEach((_, index) => {
        const itemEl = itemRefs.current[index];
        if (!itemEl) return;

        const rect = itemEl.getBoundingClientRect();
        const verticalTolerance = 60;
        if (e.clientY >= rect.top - verticalTolerance && e.clientY <= rect.bottom + verticalTolerance) {
          itemsOnRow.push({ index, rect });
        }
      });

      if (itemsOnRow.length === 0) {
        setDropPosition(null);
        return;
      }

      itemsOnRow.sort((a, b) => a.rect.left - b.rect.left);

      const GRID_GAP = 16; // Tailwind gap-4 = 16px
      const halfGap = GRID_GAP / 2;

      let newDropPosition: number | null = null;
      const firstItem = itemsOnRow[0];
      const lastItem = itemsOnRow[itemsOnRow.length - 1];

      // Expanded bounds for first/last
      const firstLeft = firstItem.rect.left - halfGap;
      const lastRight = lastItem.rect.right + halfGap;

      if (e.clientX < firstLeft) {
        newDropPosition = firstItem.index;
      } else if (e.clientX >= lastRight) {
        newDropPosition = lastItem.index + 1;
      } else {
        for (let i = 0; i < itemsOnRow.length; i++) {
          const item = itemsOnRow[i];
          const nextItem = itemsOnRow[i + 1];

          // Expand each item's detection zone by halfGap
          const itemLeft = item.rect.left - halfGap;
          const itemRight = item.rect.right + halfGap;

          // Over this item's expanded zone → decide before/after by midpoint
          if (e.clientX >= itemLeft && e.clientX < itemRight) {
            const midpoint = item.rect.left + item.rect.width / 2;
            newDropPosition = e.clientX < midpoint ? item.index : item.index + 1;
            break;
          }

          if (!nextItem) continue;

          // Expanded gap boundary between items
          const gapLeft = item.rect.right + halfGap;
          const gapRight = nextItem.rect.left - halfGap;

          if (e.clientX >= gapLeft && e.clientX < gapRight) {
            const boundary = (gapLeft + gapRight) / 2;
            newDropPosition = e.clientX < boundary ? item.index + 1 : nextItem.index;
            break;
          }
        }
      }

      if (newDropPosition === null || newDropPosition === draggedIndex || newDropPosition === draggedIndex + 1) {
        setDropPosition(null);
      } else {
        setDropPosition(newDropPosition);
      }
    } else {
      let closestIndex = -1;
      let closestDistance = Infinity;
      let insertBefore = true;

      filteredItems.forEach((_, index) => {
        const itemEl = itemRefs.current[index];
        if (!itemEl) return;

        const rect = itemEl.getBoundingClientRect();
        const distToTop = Math.abs(e.clientY - rect.top);
        const distToBottom = Math.abs(e.clientY - rect.bottom);

        if (distToTop < closestDistance) {
          closestDistance = distToTop;
          closestIndex = index;
          insertBefore = true;
        }
        if (distToBottom < closestDistance) {
          closestDistance = distToBottom;
          closestIndex = index;
          insertBefore = false;
        }
      });

      if (closestIndex === -1) {
        setDropPosition(null);
        return;
      }

      const newDropPosition = insertBefore ? closestIndex : closestIndex + 1;

      if (newDropPosition === draggedIndex || newDropPosition === draggedIndex + 1) {
        setDropPosition(null);
      } else {
        setDropPosition(newDropPosition);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedKey) return;

    const draggedIndex = filteredItems.findIndex((item) => getMediaKey(item.media_id, item.media_type) === draggedKey);
    const itemEl = itemRefs.current[index];
    if (!itemEl) return;

    const rect = itemEl.getBoundingClientRect();
    const isGrid = viewMode === 'grid';
    const mousePos = isGrid ? e.clientX : e.clientY;

    let newDropPosition: number;

    if (isGrid) {
      // Use 35% threshold for left side to make drop-before easier
      const leftThreshold = rect.left + rect.width * 0.35;

      if (mousePos < leftThreshold) {
        newDropPosition = index;
      } else {
        newDropPosition = index + 1;
      }
    } else {
      // Use 35% threshold for top side in list view
      const topThreshold = rect.top + rect.height * 0.35;

      if (mousePos < topThreshold) {
        newDropPosition = index;
      } else {
        newDropPosition = index + 1;
      }
    }

    if (newDropPosition === draggedIndex || newDropPosition === draggedIndex + 1) {
      setDropPosition(null);
    } else {
      setDropPosition(newDropPosition);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDropPosition(null);
    }
  };

  // Delayed invalidate: avoids immediate snap-back on transient errors.
  // Outbox replay will converge; this just ensures cache reconciles eventually.
  const scheduleInvalidate = useCallback(() => {
    if (invalidateTimerRef.current) return; // Already scheduled
    invalidateTimerRef.current = setTimeout(() => {
      invalidateTimerRef.current = null;
      rqRefetch();
    }, 2000);
  }, [rqRefetch]);

  // Clean up invalidate timer on unmount
  useEffect(() => {
    return () => {
      if (invalidateTimerRef.current) {
        clearTimeout(invalidateTimerRef.current);
      }
    };
  }, []);

  // Serialized move flush: drains pendingMovesRef FIFO, one at a time.
  // Per-item coalescing: moves for the same movedItemId are replaced (last wins),
  // but moves for different items are preserved and sent in order.
  const flushPendingMoves = async () => {
    if (moveInFlightRef.current) return; // Another flush is running
    moveInFlightRef.current = true;
    setIsReorderFlushing(true);

    try {
      while (pendingMovesRef.current.length > 0) {
        const move = pendingMovesRef.current.shift()!;

        const token = session?.access_token;
        if (!token || !id) break;

        try {
          const result = await apiFetch<{ success: boolean; new_revision?: number; new_rank?: string }>(
            `/watchlists/${id}/reorder`,
            {
              method: 'PUT',
              token,
              headers: { 'Idempotency-Key': move.idempotencyKey },
              body: {
                item_id: move.movedItemId,
                insert_before_id: move.insertBeforeId,
                snapshot_revision: revisionRef.current,
              },
            }
          );

          // Success: bump revision, remove outbox intent
          if (typeof result.new_revision === 'number') {
            revisionRef.current = result.new_revision;
          }
          didAckRef.current = true;
          consecutiveFlushFailuresRef.current = 0;
          if (user?.id) {
            removeIntentIfMatches(user.id, move.intentKey, move.idempotencyKey)
              .catch((err) => console.error('[OUTBOX] Failed to remove reorder intent:', err));
          }
        } catch (err) {
          const status = err instanceof ApiError ? err.status : 0;
          const code = err instanceof ApiError ? err.code : undefined;

          if (status === 409 && code === 'REVISION_CONFLICT') {
            // Fetch fresh revision and retry once
            try {
              const freshResult = await apiFetch<{ watchlist: { revision?: number } }>(
                `/watchlists/${id}?page=1&limit=1`,
                { token }
              );
              const freshRevision = freshResult?.watchlist?.revision;

              if (typeof freshRevision === 'number') {
                revisionRef.current = freshRevision;
                const retryResult = await apiFetch<{ success: boolean; new_revision?: number }>(
                  `/watchlists/${id}/reorder`,
                  {
                    method: 'PUT',
                    token,
                    headers: { 'Idempotency-Key': move.idempotencyKey },
                    body: {
                      item_id: move.movedItemId,
                      insert_before_id: move.insertBeforeId,
                      snapshot_revision: freshRevision,
                    },
                  }
                );
                if (typeof retryResult.new_revision === 'number') {
                  revisionRef.current = retryResult.new_revision;
                }
                didAckRef.current = true;
                if (user?.id) {
                  removeIntentIfMatches(user.id, move.intentKey, move.idempotencyKey)
                    .catch((err2) => console.error('[OUTBOX] Failed to remove reorder intent after retry:', err2));
                }
                continue; // Retry succeeded, process next queued move
              }
            } catch (retryErr) {
              console.error('Reorder retry after 409 failed:', retryErr);
            }
            // Retry failed — toast only once (not per-move), leave outbox to handle
            consecutiveFlushFailuresRef.current++;
            rqRefetch();
            break; // Stop processing queue
          }

          // Transient error: do NOT rollback optimistic UI (no bounce).
          // Leave intent in outbox for replay; delayed invalidate reconciles later.
          console.error('[REORDER] Transient error, outbox will converge:', err);
          consecutiveFlushFailuresRef.current++;
          if (consecutiveFlushFailuresRef.current >= 3) {
            pendingMovesRef.current = [];
            // Clear stale reorder intents from outbox to prevent replay of outdated moves
            if (user?.id && id) {
              getOutboxIntents(user.id).then((intents) => {
                for (const [key, intent] of intents) {
                  if (intent.intentType === 'reorderMove' && intent.watchlistId === id) {
                    removeIntent(user.id, key).catch(() => {});
                  }
                }
              }).catch(() => {});
            }
            toast.error('Reorder failed — please refresh');
          }
          scheduleInvalidate();
          break; // Stop processing queue
        }
      }
    } finally {
      moveInFlightRef.current = false;
      // If new moves arrived during flight, kick off another flush
      if (pendingMovesRef.current.length > 0) {
        flushPendingMoves();
      } else {
        setIsReorderFlushing(false);
        consecutiveFlushFailuresRef.current = 0;
        // Persist cache immediately after queue drains so hard refresh can't lose reorder
        if (didAckRef.current) {
          didAckRef.current = false;
          try { flushNow(); } catch (_) { /* best-effort */ }
        }
      }
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();

    if (!draggedKey || !watchlist || !id || dropPosition === null) {
      setDropPosition(null);
      setDraggedKey(null);
      return;
    }

    const sourceIndex = filteredItems.findIndex((item) => getMediaKey(item.media_id, item.media_type) === draggedKey);

    let targetIndex = dropPosition;
    if (sourceIndex < dropPosition) {
      targetIndex = dropPosition - 1;
    }

    if (sourceIndex === targetIndex) {
      setDropPosition(null);
      setDraggedKey(null);
      return;
    }

    // Build new ordered items array
    const newItems = [...items];
    const actualSourceIndex = newItems.findIndex((it) => getMediaKey(it.media_id, it.media_type) === draggedKey);
    if (actualSourceIndex === -1) {
      setDropPosition(null);
      setDraggedKey(null);
      return;
    }
    const [removed] = newItems.splice(actualSourceIndex, 1);
    newItems.splice(targetIndex, 0, removed);

    // Compute movedItemId and insert_before_id deterministically:
    // After splicing, the moved item is at targetIndex in newItems.
    // insert_before_id = the item AFTER the moved item (or null if moved to end).
    const movedIndex = targetIndex;
    const movedItemId = removed.id;
    const insertBeforeId = (movedIndex + 1 < newItems.length)
      ? newItems[movedIndex + 1].id
      : null;

    if (!movedItemId) {
      setDropPosition(null);
      setDraggedKey(null);
      toast.error('Cannot save order - item is still syncing');
      return;
    }

    // Phase 4A: Optimistic reorder via React Query cache
    optimisticUpdates.reorderItems(id, newItems);

    setDropPosition(null);
    setDraggedKey(null);

    // Enqueue to outbox for durability (survives refresh/unload)
    const intentKey = getReorderIntentKeyForLookup(id, movedItemId);
    let idempotencyKey: string = crypto.randomUUID();
    if (user?.id) {
      try {
        const intent = await enqueueReorderIntent(user.id, {
          watchlistId: id,
          itemId: movedItemId,
          insertBeforeId,
          snapshotRevision: revisionRef.current,
        });
        idempotencyKey = intent.idempotencyKey;
      } catch (err) {
        console.error('[OUTBOX] Failed to enqueue reorder:', err);
      }
    }

    // Enqueue move with per-item coalescing (last-move-wins per movedItemId)
    const queue = pendingMovesRef.current;
    const existingIdx = queue.findIndex((m) => m.movedItemId === movedItemId);
    const move = { movedItemId, insertBeforeId, intentKey, idempotencyKey };
    if (existingIdx !== -1) {
      queue[existingIdx] = move; // Replace in-place (keep queue position)
    } else {
      queue.push(move);
    }
    flushPendingMoves();
  };

  // Disable drag reordering when filter is active, not editor/owner, list not fully loaded,
  // or a reorder flush is in-flight (prevents jitter from additional commits during serialized send)
  const isCustomSort = sortBy === 'position' && mediaTypeFilter === 'all' && !popularOnly && canEdit &&
    !isReorderFlushing && !hasNextPage && !isFetchingNextPage &&
    filters.genres.length === 0 &&
    filters.languages.length === 0 &&
    filters.yearRange[0] === 1920 && filters.yearRange[1] === new Date().getFullYear() &&
    filters.runtimeRange[0] === 0 && filters.runtimeRange[1] === 300 &&
    filters.ourRatingRange[0] === 0 && filters.ourRatingRange[1] === 10 &&
    filters.ratingRange[0] === 0 && filters.ratingRange[1] === 10 &&
    filters.rtRatingRange[0] === 0 && filters.rtRatingRange[1] === 100 &&
    // STRICT v6.1: optimistic items may have falsy id; disable reorder until synced
    filteredItems.every((it) => !!it.id && !it.id.startsWith('temp-'));


  const handleMovieClick = (item: WatchlistItemRow) => {
    navigate(`/${item.media_type}/${item.media_id}`);
  };

  // Note edit handlers
  const handleStartEditNote = (mediaKey: MediaKey) => {
    const meta = entryMeta.get(mediaKey);
    if (!meta?.itemId || meta.itemId.startsWith('temp-')) {
      toast.error('Cannot edit note - item still syncing');
      return;
    }
    setEditingNoteItemId(mediaKey);
    setNoteText(meta?.notes || '');
  };

  const handleCancelEditNote = () => {
    setEditingNoteItemId(null);
    setNoteText('');
  };

  const handleSaveNote = async (mediaKey: MediaKey) => {
    if (!watchlist || !id || !session?.access_token) return;

    const [mediaType, mediaIdStr] = mediaKey.split(':') as ['movie' | 'tv', string];
    const mediaId = parseInt(mediaIdStr, 10);

    const meta = entryMeta.get(mediaKey);
    if (!meta?.itemId) {
      toast.error('Cannot save note - item still syncing');
      return;
    }

    const trimmedNote = noteText.trim();
    const noteSnapshot = meta.notes;

    // Phase 4A: Optimistic update via React Query cache
    optimisticUpdates.updateItemNote(id, mediaId, mediaType, trimmedNote || null);
    setEditingNoteItemId(null);
    setNoteText('');

    try {
      await apiFetch(
        `/watchlists/${watchlist.id}/items/${meta.itemId}/note`,
        {
          method: 'PATCH',
          token: session.access_token,
          body: { note: trimmedNote },
        }
      );
    } catch (err) {
      // Rollback: restore original note
      optimisticUpdates.updateItemNote(id, mediaId, mediaType, noteSnapshot ?? null);
      toast.error('Failed to save note');
    }
  };

  // Follow/Unfollow handler (Phase 3.1 - uses React Query mutation)
  const handleToggleFollow = async () => {
    if (!watchlist || !session?.access_token || followToggleLoading) return;

    const wasFollowing = isFollowing;
    // Pass owner info for optimistic Sidebar rendering
    const success = await toggleFollow(watchlist.id, watchlist.name, wasFollowing, {
      owner: {
        id: listOwner?.id,
        username: listOwner?.username,
      },
      media_filter: (watchlist as any).media_filter,
    });

    if (success === 'SOCIAL_TIER_REQUIRED') {
      setUpgradeModalFeature('Following lists');
    } else if (success) {
      toast.success(wasFollowing ? 'Unfollowed list' : 'Following list');
    } else {
      toast.error('Something went wrong');
    }
  };

  // Default list skeleton while UserLibraryContext hydrates (default ownership)
  if (listOwner && watchlist && canEdit && watchlist.is_default && !isHydrated) {
    return (
      <div className="min-h-screen pb-16">
        <div className="bg-[#0F1419] border-b border-white/[0.06] sticky top-16 z-20">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="h-7 w-48 bg-slate-800 rounded animate-pulse" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="aspect-[2/3] bg-slate-800 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Phase 4A: Loading UX - show skeleton only when no items and loading
  // React Query provides cache-first rendering - no flash on revisit
  const shouldShowSkeleton = rqLoading && items.length === 0 && !watchlist;

  if (shouldShowSkeleton) {
    return (
      <div className="min-h-screen pb-16">
        <div className="bg-[#0F1419] border-b border-white/[0.06] sticky top-16 z-20">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="h-7 w-48 bg-slate-800 rounded animate-pulse" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="aspect-[2/3] bg-slate-800 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-[#1A1F26] text-white rounded-lg hover:bg-slate-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!watchlist) {
    return null;
  }

  // Logged-out users can view public non-default lists, but default personal lists require auth.
  if (!user && watchlist.is_default) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <p className="text-white text-lg font-medium mb-2">Sign in to view this list</p>
          <p className="text-slate-400 text-sm mb-6">Personal lists are only available to the account owner.</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white rounded-lg transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Get label for count
  const getCountLabel = () => {
    if (mediaTypeFilter === 'movie') return 'movies';
    if (mediaTypeFilter === 'tv') return 'TV shows';
    return 'items';
  };

  return (
    <div className="min-h-screen bg-[#0F1419]">
      {/* FilterBar at top - same as Discover page */}
      <FilterBar
        filters={filters}
        onChange={setFilters}
        mediaType={watchlist.media_filter === 'tv' ? 'tv' : 'movie'}
      />

      {/* Title row + Controls */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left: Cover + Title + Settings */}
          <div className="flex items-center gap-4">
            {/* Cover thumbnail (2:3 poster aspect) */}
            {watchlist.cover_url && (
              <img
                src={watchlist.cover_url}
                alt=""
                className="w-10 h-[60px] rounded-lg object-cover ring-1 ring-white/[0.08]"
              />
            )}
            <div>
              <h1 className="text-2xl font-bold text-white">{displayTitle}</h1>
              {!watchlist.is_default && watchlist.owner?.username && (
                <p className="text-sm text-slate-400">
                  by{' '}
                  <Link
                    to={`/u/${watchlist.owner.username}`}
                    className="text-teal-400 hover:text-teal-300 transition-colors"
                  >
                    @{watchlist.owner.username}
                  </Link>
                </p>
              )}
              <p className="text-sm text-slate-500">
                {watchlist.item_count ?? 0} items • {watchlist.follower_count ?? 0} followers
              </p>
            </div>

            {/* Settings button (owner or collaborator, custom lists only) */}
            {(isOwner || !!collaboratorRole) && !watchlist.is_default && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    if (isOwner && isFree) {
                      setUpgradeModalFeature('Managing collaborators');
                      return;
                    }
                    setShowCollaboratorsModal(true);
                  }}
                  className={`p-2 rounded-lg transition-colors ${
                    isOwner && isFree
                      ? 'text-slate-600 hover:text-slate-400'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                  title={isOwner ? (isFree ? 'Upgrade to Social to manage collaborators' : 'Manage collaborators') : 'View collaborators'}
                >
                  <Users size={18} />
                </button>
                {isOwner && (
                <button
                  onClick={() => setShowEditListModal(true)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                  title="List settings"
                >
                  <Settings size={18} />
                </button>
                )}
              </div>
            )}

            {/* Media type filter tabs */}
            {(stableMovieCount > 0 || stableTvCount > 0) && (
              <div className="flex items-center bg-slate-700/50 rounded-full p-1">
                <button
                  onClick={() => setMediaTypeFilter('all')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                    mediaTypeFilter === 'all'
                      ? 'bg-gradient-to-r from-slate-500 to-slate-600 text-white shadow-lg'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  All
                </button>
                {stableMovieCount > 0 && (
                  <button
                    onClick={() => setMediaTypeFilter('movie')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                      mediaTypeFilter === 'movie'
                        ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Film className="w-3 h-3" />
                    Movies ({stableMovieCount})
                  </button>
                )}
                {stableTvCount > 0 && (
                  <button
                    onClick={() => setMediaTypeFilter('tv')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                      mediaTypeFilter === 'tv'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Tv className="w-3 h-3" />
                    TV ({stableTvCount})
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Right: Controls */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Updated indicator for followed lists */}
            {!isOwner && isFollowing && listUpdatedAt && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">
                  Updated {formatRelativeTime(listUpdatedAt)}
                </span>
                <button
                  onClick={() => rqRefetch()}
                  disabled={rqFetching}
                  className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                  title="Refresh list"
                >
                  <RefreshCw className={`w-4 h-4 ${rqFetching ? 'animate-spin' : ''}`} />
                </button>
              </div>
            )}

            {/* Follow button (for non-owned public lists) */}
            {!!session?.access_token && !isOwner && watchlist.visibility === 'public' && (
              isFree ? (
                <button
                  onClick={() => setUpgradeModalFeature('Following lists')}
                  className="px-3 py-2 rounded-lg bg-slate-700 text-slate-400 border border-slate-600 opacity-75 hover:opacity-100 transition-opacity flex items-center gap-1.5"
                  title="Upgrade to Social to follow lists"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="text-sm">Follow</span>
                </button>
              ) : (
              <button
                onClick={handleToggleFollow}
                disabled={followToggleLoading || followState === 'unknown'}
                className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                  followState === 'unknown'
                    ? 'bg-[#1A1F26] text-slate-300 border border-white/[0.08]'
                    : isFollowing
                    ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/50'
                    : 'bg-teal-500 text-white hover:bg-teal-400'
                } ${followToggleLoading || followState === 'unknown' ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={
                  followState === 'unknown'
                    ? 'Checking follow state…'
                    : isFollowing
                      ? 'Unfollow this list'
                      : 'Follow this list'
                }
              >
                {followState === 'unknown' ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Checking</span>
                  </>
                ) : isFollowing ? (
                  <>
                    <UserMinus className="w-4 h-4" />
                    <span className="text-sm">Following</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span className="text-sm">Follow</span>
                  </>
                )}
              </button>
              )
            )}

            {/* Share button - shown for public and friends lists */}
            {(watchlist.visibility === 'public' || watchlist.visibility === 'friends') && (
              <ShareButton
                path={`/list/${watchlist.id}`}
                title={displayTitle}
                image={items[0]?.media_poster ? getPosterUrl(items[0].media_poster, 'large') : undefined}
                variant="icon-only"
              />
            )}

            {/* Add Movie button - owner/editor only (must be authed) */}
            {!!session?.access_token && canEdit && (
              <button
                onClick={() => {
                  if (isFree && watchlist && !watchlist.is_default) {
                    setUpgradeModalFeature('Adding to custom lists');
                    return;
                  }
                  setShowAddMovieModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white rounded-lg transition-colors font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Movie
              </button>
            )}

            {/* Popular only toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">Popular only</span>
              <button
                onClick={() => setPopularOnly(!popularOnly)}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  popularOnly ? 'bg-teal-500' : 'bg-slate-600'
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    popularOnly ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Sort dropdown */}
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => {
                  const newSort = e.target.value as SortOption;
                  setSortBy(newSort);
                  if (newSort === 'position') {
                    setSortOrder('asc');
                  }
                }}
                className="bg-[#1A1F26] text-white text-sm rounded-lg px-3 py-2 ring-1 ring-white/[0.08] focus:outline-none focus:border-teal-500"
              >
                {SORT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="p-2 bg-[#1A1F26] rounded-lg hover:bg-slate-700 transition-colors"
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortOrder === 'asc' ? (
                  <SortAsc className="w-5 h-5 text-slate-400" />
                ) : (
                  <SortDesc className="w-5 h-5 text-slate-400" />
                )}
              </button>
            </div>

            {/* View mode toggle */}
            <div className="flex bg-[#1A1F26] rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-teal-500 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-teal-500 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Count row - only show when filtering is active */}
        {isFilteredView && (
          <div className="mt-2">
            <p className="text-sm text-slate-400">
              <span className="text-white font-medium">{filteredItems.length}</span>{' '}
              {getCountLabel()} found
            </p>
          </div>
        )}

        {isPartialClientSort && (
          <div className="max-w-7xl mx-auto px-4 mb-2">
            <p className="text-xs text-amber-400/70">
              Sort applied to loaded items. Scroll down to load all items for complete sorting.
            </p>
          </div>
        )}
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 pb-6">
        {rqLoading && items.length === 0 && watchlist ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="aspect-[2/3] bg-slate-800 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          /* Empty state - no items at all */
          <div className="text-center py-16">
            <p className="text-slate-300 text-lg font-medium mb-2">No items found</p>
            <p className="text-slate-500 text-sm mb-6">Start adding movies or TV shows to your list</p>
            {isOwner && (
              <button
                onClick={() => {
                  if (isFree && watchlist && !watchlist.is_default) {
                    setUpgradeModalFeature('Adding to custom lists');
                    return;
                  }
                  setShowAddMovieModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Your First Movie
              </button>
            )}
          </div>
        ) : filteredItems.length === 0 ? (
          /* Empty state - filters applied */
          <div className="text-center py-16">
            <p className="text-slate-300 text-lg font-medium mb-2">No items found</p>
            <p className="text-slate-500 text-sm mb-6">Try adjusting your filters</p>
            <button
              onClick={() => {
                setMediaTypeFilter('all');
                setPopularOnly(false);
                setFilters(DEFAULT_FILTERS);
              }}
              className="px-4 py-2 bg-[#1A1F26] text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div
            ref={gridContainerRef}
            className="relative grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
            onDragOver={handleContainerDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Invisible drop zone for left margin - finds first item on current row */}
            {isCustomSort && draggedKey && (
              <div
                className="fixed left-0 top-0 bottom-0 z-40"
                style={{ width: gridContainerRef.current?.getBoundingClientRect().left ?? 120, minWidth: '40px' }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  // Only trigger if cursor is actually to the left of the grid
                  const gridLeft = gridContainerRef.current?.getBoundingClientRect().left ?? 0;
                  if (e.clientX >= gridLeft) return; // Cursor is over grid, let grid handle it

                  // Find which row the mouse is on and set drop position to first item of that row
                  const itemsOnRow: { index: number; rect: DOMRect }[] = [];
                  filteredItems.forEach((_, index) => {
                    const itemEl = itemRefs.current[index];
                    if (!itemEl) return;
                    const rect = itemEl.getBoundingClientRect();
                    const verticalTolerance = 60;
                    if (e.clientY >= rect.top - verticalTolerance && e.clientY <= rect.bottom + verticalTolerance) {
                      itemsOnRow.push({ index, rect });
                    }
                  });
                  if (itemsOnRow.length > 0) {
                    itemsOnRow.sort((a, b) => a.rect.left - b.rect.left);
                    const firstOnRow = itemsOnRow[0];
                    // Only trigger if mouse is actually to the LEFT of the first card
                    if (e.clientX < firstOnRow.rect.left) {
                      const draggedIndex = filteredItems.findIndex((item) => getMediaKey(item.media_id, item.media_type) === draggedKey);
                      if (firstOnRow.index !== draggedIndex && firstOnRow.index !== draggedIndex + 1) {
                        setDropPosition(firstOnRow.index);
                      } else {
                        setDropPosition(null);
                      }
                    }
                  }
                }}
                onDrop={handleDrop}
              />
            )}
            {filteredItems.map((item, index) => (
              <div
                key={getMediaKey(item.media_id, item.media_type)}
                ref={(el) => { itemRefs.current[index] = el; }}
                className={`group relative transition-all ${draggedKey === getMediaKey(item.media_id, item.media_type) ? 'opacity-50' : ''}`}
                draggable={isCustomSort && !!item.id}
                onDragStart={(e) => handleDragStart(e, item)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => { e.stopPropagation(); handleDragOver(e, index); }}
              >
                {/* Drop indicator - before this item */}
                {dropPosition === index && (
                  <div className="absolute -left-2 top-0 bottom-0 w-1 bg-teal-500 rounded-full z-10 shadow-[0_0_8px_rgba(20,184,166,0.6)]" />
                )}
                {/* Drop indicator - after last item */}
                {dropPosition === filteredItems.length && index === filteredItems.length - 1 && (
                  <div className="absolute -right-2 top-0 bottom-0 w-1 bg-teal-500 rounded-full z-10 shadow-[0_0_8px_rgba(20,184,166,0.6)]" />
                )}

                {/* MovieCard wrapper - positioning context for overlays */}
                <div className="relative">
                  <MovieCard
                    movie={toMediaItem(item)}
                    onClick={() => !draggedKey && handleMovieClick(item)}
                    showQuickActions={!canEdit}
                  />

                  {/* Drag handle - positioned relative to MovieCard */}
                  {isCustomSort && (
                    <div
                      className="absolute top-2 left-2 p-1.5 bg-[#0F1419]/80 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-20"
                      title="Drag to reorder"
                    >
                      <GripVertical className="w-4 h-4 text-white" />
                    </div>
                  )}

                  {/* Remove button - owner/editor only (disabled until itemId exists) */}
                  {canEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!item.id) return;
                        handleRemoveItem(getMediaKey(item.media_id, item.media_type));
                      }}
                      disabled={!item.id}
                      className={`absolute top-2 right-2 p-2 bg-red-500/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 z-20 ${
                        !item.id ? 'cursor-not-allowed opacity-30 group-hover:opacity-30' : ''
                      }`}
                      title="Remove from list"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List view */
          <div
            className="relative space-y-2"
            onDragOver={handleContainerDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Invisible drop zone for top area in list view */}
            {isCustomSort && draggedKey && (
              <div
                className="fixed left-0 right-0 top-0 h-32 z-50"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const draggedIndex = filteredItems.findIndex((item) => getMediaKey(item.media_id, item.media_type) === draggedKey);
                  if (draggedIndex !== 0 && draggedIndex !== 1) {
                    setDropPosition(0);
                  } else {
                    setDropPosition(null);
                  }
                }}
                onDrop={handleDrop}
              />
            )}
            {filteredItems.map((item, index) => (
              <div
                key={getMediaKey(item.media_id, item.media_type)}
                ref={(el) => { itemRefs.current[index] = el; }}
                className={`relative flex items-center gap-4 p-3 bg-[#1A1F26]/50 rounded-lg hover:bg-[#1A1F26] transition-all cursor-pointer group ${
                  draggedKey === getMediaKey(item.media_id, item.media_type) ? 'opacity-50' : ''
                }`}
                draggable={isCustomSort && !!item.id}
                onDragStart={(e) => handleDragStart(e, item)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => { e.stopPropagation(); handleDragOver(e, index); }}
                onClick={() => !draggedKey && handleMovieClick(item)}
              >
                {/* Drop indicator - before this item */}
                {dropPosition === index && (
                  <div className="absolute -top-1.5 left-0 right-0 h-1 bg-teal-500 rounded-full z-10 shadow-[0_0_8px_rgba(20,184,166,0.6)]" />
                )}
                {/* Drop indicator - after last item */}
                {dropPosition === filteredItems.length && index === filteredItems.length - 1 && (
                  <div className="absolute -bottom-1.5 left-0 right-0 h-1 bg-teal-500 rounded-full z-10 shadow-[0_0_8px_rgba(20,184,166,0.6)]" />
                )}
                {/* Drag handle */}
                {isCustomSort && (
                  <div
                    className="flex-shrink-0 p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing"
                    title="Drag to reorder"
                  >
                    <GripVertical className="w-5 h-5" />
                  </div>
                )}

                {/* Poster thumbnail */}
                <div className="w-12 h-18 flex-shrink-0 rounded overflow-hidden bg-slate-700">
                  {item.media_poster ? (
                    <img
                      src={getPosterUrl(item.media_poster, 'small')}
                      alt={item.media_title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl">
                      🎬
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-medium group-hover:text-teal-400 transition-colors">
                    {item.media_title}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    {item.media_year && <span>{item.media_year}</span>}
                    {item.media_type === 'tv' && (
                      <span className="px-1.5 py-0.5 bg-purple-600/50 text-purple-300 text-xs rounded">
                        TV
                      </span>
                    )}
                    {(() => {
                      const ourRating = calculateOurRating(item.imdb_rating, item.rt_rating);
                      return (
                        <>
                          {ourRating && (
                            <span className="flex items-center gap-0.5 text-xs">
                              <span className="text-purple-500">⭐</span>
                              <span>{ourRating.toFixed(1)}</span>
                            </span>
                          )}
                          {item.imdb_rating && (
                            <span className="flex items-center gap-0.5 text-xs">
                              <span className="text-yellow-500">★</span>
                              <span>{item.imdb_rating.toFixed(1)}</span>
                            </span>
                          )}
                          {item.rt_rating && (
                            <span className="flex items-center gap-0.5 text-xs">
                              <span className="text-red-500">🍅</span>
                              <span>{item.rt_rating}%</span>
                            </span>
                          )}
                        </>
                      );
                    })()}
                    {item.added_by_username && !watchlist.is_default && (watchlist.has_collaborators || watchlist.collaborator_role) && (
                      <Link
                        to={`/u/${item.added_by_username}`}
                        className="text-xs text-teal-400 hover:text-teal-300 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        added by @{item.added_by_username}
                      </Link>
                    )}
                  </div>
                  {/* Note display */}
                  {item.notes && editingNoteItemId !== getMediaKey(item.media_id, item.media_type) && (
                    <div className="mt-1 text-xs text-slate-500 italic truncate max-w-md">
                      "{item.notes}"
                    </div>
                  )}
                  {/* Note edit form */}
                  {editingNoteItemId === getMediaKey(item.media_id, item.media_type) && (
                    <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add a note..."
                        className="flex-1 bg-slate-700 text-white text-sm rounded px-2 py-1 border border-slate-600 focus:outline-none focus:border-teal-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveNote(getMediaKey(item.media_id, item.media_type));
                          if (e.key === 'Escape') handleCancelEditNote();
                        }}
                      />
                      <button
                        onClick={() => handleSaveNote(getMediaKey(item.media_id, item.media_type))}
                        className="px-2 py-1 bg-teal-500 text-white text-xs rounded hover:bg-teal-400"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEditNote}
                        className="p-1 text-slate-400 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Note button - owner/editor only (disabled until itemId exists) */}
                {canEdit && editingNoteItemId !== getMediaKey(item.media_id, item.media_type) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!item.id) return;
                      handleStartEditNote(getMediaKey(item.media_id, item.media_type));
                    }}
                    disabled={!item.id}
                    className={`p-2 text-slate-400 hover:text-teal-400 transition-colors ${
                      item.notes ? '' : 'opacity-0 group-hover:opacity-100'
                    } ${!item.id ? 'cursor-not-allowed opacity-30 group-hover:opacity-30 hover:text-slate-400' : ''}`}
                    title={item.notes ? 'Edit note' : 'Add note'}
                  >
                    <MessageSquare className="w-4 h-4" />
                  </button>
                )}

                {/* Added date */}
                <div className="text-sm text-slate-500">
                  {new Date(item.added_at).toLocaleDateString()}
                </div>

                {/* Remove button - owner/editor only (disabled until itemId exists) */}
                {canEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!item.id) return;
                      handleRemoveItem(getMediaKey(item.media_id, item.media_type));
                    }}
                    disabled={!item.id}
                    className={`p-2 text-slate-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-colors ${
                      !item.id ? 'cursor-not-allowed opacity-30 group-hover:opacity-30 hover:text-slate-400' : ''
                    }`}
                    title="Remove from list"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Infinite scroll sentinel */}
        {watchlist && hasNextPage && (
          <div
            ref={sentinelRef}
            className="flex justify-center py-8"
          >
            {isFetchingNextPage && (
              <div className="flex items-center gap-2 text-slate-400">
                <div className="w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                <span>Loading more…</span>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Rating Modal */}
      {selectedMovie && (
        <RatingModal
          movie={selectedMovie}
          isOpen={isRatingModalOpen}
          onClose={() => {
            setIsRatingModalOpen(false);
            setSelectedMovie(null);
          }}
        />
      )}

      {/* Add Movie Modal */}
      {watchlist && id && (
        <AddMovieModal
          isOpen={showAddMovieModal}
          onClose={() => setShowAddMovieModal(false)}
          watchlistId={watchlist.id}
          watchlistName={watchlist.name}
          mediaFilter={watchlist.media_filter}
          existingMediaIds={existingMediaIds}
          isDefaultList={watchlist.is_default}
          listSlug={watchlist.slug as 'watched' | 'watchlist' | 'favorites' | null}
          onRefetchNeeded={() => {
            // Phase 4A: Refetch via React Query
            rqRefetch();
          }}
          // NOTE: onItemAdded removed - mutation is the single source of truth for cache writes
          // The mutation's onMutate/onSuccess handle optimistic updates with enriched data
        />
      )}

      {/* Edit List Modal (owner only, custom lists only) */}
      {watchlist && isOwner && !watchlist.is_default && (
        <EditListModal
          isOpen={showEditListModal}
          onClose={() => setShowEditListModal(false)}
          watchlist={watchlist}
          onDeleted={() => {
            // Navigate to home after deletion
            navigate('/');
          }}
          posterCandidates={posterCandidates}
        />
      )}

      {/* Manage Collaborators Modal (owner or collaborator, custom lists only) */}
      {watchlist && (isOwner || !!collaboratorRole) && !watchlist.is_default && (
        <ManageCollaboratorsModal
          isOpen={showCollaboratorsModal}
          onClose={() => setShowCollaboratorsModal(false)}
          listId={watchlist.id}
          listName={watchlist.name}
          isOwner={isOwner}
        />
      )}

      {/* Social Upgrade Modal */}
      <SocialUpgradeModal
        isOpen={!!upgradeModalFeature}
        onClose={() => setUpgradeModalFeature(null)}
        feature={upgradeModalFeature || ''}
      />
    </div>
  );
}
